import { useEffect, useRef, useState } from "react";
import apiInstance from "../../apiInstance";
import PopupModal from "../Popups/PopupModal";
import SubscriptionsTable from "./SubscriptionsTable";
import DetailsPage from "./DetailsPage/DetailsPage";

const DevadminSubscriptions = () => {
    const [subscriptions, setSubscriptions] = useState([]);
    const [totalSubscriptions, setTotalSubscriptions] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(50);
    const [selectedItems, setSelectedItems] = useState([]);
    const [bulkAction, setBulkAction] = useState("");
    const [reload, setReload] = useState(0);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [filters, setFilters] = useState({
        status: ""
    });
    const [error, setError] = useState("");
    const popupRef = useRef();

    const addSubscriptionHandler = (sub) => {
        setSubscriptions((prev) => [sub, ...prev]);
    }

    const deleteSubscriptionHandler = (id) => {
		setSubscriptions((prev) => prev.filter(item => item.id !== id));
    }

    const updateSubscriptionsHandler = (sub) => {
        const updatedSubs = subscriptions.map(item => item.id === sub.id ? {...item, ...sub} : item);
        setSubscriptions(updatedSubs)
    }

    const handleSelectAll = (e) => {
        if(e.target.checked) {
            const allIds = subscriptions.map((item) => item._id);
            setSelectedItems(allIds);
        } else {
            setSelectedItems([]);
        }
    }

    const handleSelect = (selectedId) => {
        setSelectedItems(prevSelected => {
            if (prevSelected.includes(selectedId)) {
                return prevSelected.filter(id => selectedId !== id);
            } else {
                return [...prevSelected, selectedId];
            }
        });
    };

    const handleBulkAction = async () => {
        switch(bulkAction) {
            case "delete":
                
                for (const id of selectedItems) {
                    try {
                        const res = await apiInstance.delete(`/devadmin/subscription/${id}`);

                        setSubscriptions(prev =>
                            prev.filter(item => item.id !== id)
                        );
                    } catch (error) {
                        console.error(`Error deleting subscription:`, error);
                        setError(`Error deleting subscription ${id}.`);
                    }
                }
                setReload(prev => prev + 1);
                break;
        }
    }

    const handleCurrentPageChange = (p) => {
        const totalPages = Math.ceil(totalSubscriptions / perPage);
        const value = Math.min(totalPages, Math.max(1, p));
        setCurrentPage(value);
        setSelectedItems([]);
    }

    const renderPaginationControls = () => {

        let totalPages = 0;
        let startItem = 0;
        let endItem = 0;
        if(totalSubscriptions > 0) {
            totalPages = Math.ceil(totalSubscriptions / perPage);
            startItem = (currentPage - 1) * perPage + 1;
            endItem = Math.min(currentPage * perPage, totalSubscriptions);
        }

        return (
            <div className="mjsdev-webs-pagination-wrapper">
                Per Page
                <select 
                    value={perPage} 
                    onChange={(e) => {
                        setPerPage(Number(e.target.value));
                        handleCurrentPageChange(1); 
                    }} 
                >
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={500}>500</option>
                </select>
                <span>
                    Showing {startItem}-{endItem} of {totalSubscriptions}
                </span>
                <button onClick={() => handleCurrentPageChange(1)} disabled={currentPage === 1}>
                    &laquo; 
                </button>
                <button 
                    onClick={() => handleCurrentPageChange(currentPage - 1)} 
                    disabled={currentPage === 1}
                >
                    &lsaquo; 
                </button>                
                <input
                    type="number"
                    value={currentPage}
                    onChange={(e) => handleCurrentPageChange(Number(e.target.value))}
                />
                <span>
                    of {totalPages}
                </span>
                <button 
                    onClick={() => handleCurrentPageChange(currentPage + 1)} 
                    disabled={currentPage === totalPages}
                >
                    &rsaquo;
                </button>
                <button onClick={() => handleCurrentPageChange(totalPages)} disabled={currentPage === totalPages}>
                    &raquo;
                </button>
            </div>
        );
    };

    useEffect(() => {
        const timeout = setTimeout(() => {
            setDebouncedSearch(search || "");
        }, 400); 

        return () => clearTimeout(timeout);
    }, [search]);

    useEffect(() => {
        const fetchSubs = async () => {
            apiInstance
                .get(`/devadmin/subscription`, {
                    params: {
                        page: currentPage,
                        limit: perPage,
                        search: debouncedSearch,
                        ...filters
                    }
                })
                .then((response) => {
                    const fetchedSubs = response.data.subscriptions || [];
                    setSubscriptions(fetchedSubs);
                    setTotalSubscriptions(response.data.pagination.total || 0);
                    setSelectedItems([]);
                    
                })
                .catch((error) => {
                    console.error("Error fetching data:", error);
                    setError("Failed to load subscriptions.");
                });
        }
        fetchSubs();
		
    }, [currentPage, perPage, reload, filters, debouncedSearch]);

    return (
        <>
            <div className="mjsdev-main">
                <div className="mjsdev-webs-header">
                    <h1></h1>
                    <div>
                        <button 
                            className="mjs-btn"
                            onClick={(e) => {
                                if (popupRef.current) {
                                    popupRef.current.openPopup(
                                        "addSubscription"
                                    );
                                }
                            }}    
                        >Add New Subscription</button>
                    </div>
                </div>

                <div className="mjsdev-webs-toolbar">
                    <div className="mjsdev-webs-bulk_actions">
                        <select
                            value={bulkAction}
                            onChange={(e) => setBulkAction(e.target.value)}
                        >
                            <option value="">Select</option>
                            <option value="delete">Delete</option>
                        </select>
                        <button
                            className="mjs-btn"
                            onClick={handleBulkAction}
                            disabled={!bulkAction || selectedItems.length === 0}
                        >
                            Apply
                        </button>
                    </div>
                    
                    <div className="mjsdev-webs-filters">
                        <div className="mjsdev-search-wrapper">
                            <input
                                type="text"
                                placeholder="Search..."
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setCurrentPage(1); 
                                }}
                            />
                            {search && (
                                <i
                                    className="fa-solid fa-times"
                                    onClick={() => setSearch("")}
                                    title="Clear search"
                                ></i>
                            )}
                        </div>
                        <select
                            value={filters.status}
                            onChange={(e) => {
                                setFilters((prev) => ({
                                    ...prev,
                                    status: e.target.value
                                }))
                                setCurrentPage(1); 
                            }}
                        >
                            <option value="">All Status</option>
                            <option value="active">Active</option>
                            <option value="paused">Paused</option>
                            <option value="canceled">Canceled</option>
                            <option value="incomplete">Incomplete</option>
                            <option value="past_due">Past Due</option>
                            <option value="expired">Expired</option>
                        </select>
                    </div>

                    <div className="mjsdev-webs-pgn-top">
                        {renderPaginationControls()}
                    </div>
                </div>

                {error && <p className="mjs-error">{error}</p>}

                <SubscriptionsTable
                    bulkSelect={true}
                    subscriptions={subscriptions}
                    selectedItems={selectedItems}
                    handleSelectAll={handleSelectAll}
                    handleSelect={handleSelect}
                    onEdit={(sub) => {
                        popupRef.current?.openPopup("editSubscription", { subscription: sub });
                    }}
                    onDelete={(subId) => {
                        popupRef.current?.openPopup("deleteSubscription", { subId: subId });
                    }}
                />
                
                <div className="mjsdev-webs-pgn-bottom">
                    {renderPaginationControls()}
                </div>
            </div>
            <PopupModal
                ref={popupRef}
                addSubscription={addSubscriptionHandler}
                deleteSubscription={deleteSubscriptionHandler}
                updateSubscription={updateSubscriptionsHandler}
            />
        </>
    );
};

export default DevadminSubscriptions;
